import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAnimeDetail, readJson, searchAnime } from './lib/api';
import {
  closeActivity,
  initializeDiscord,
  isDiscordActivityEnvironment,
} from './lib/discord';

const emptyParty = {
  selectedAnime: null,
  selectedEpisode: null,
  isPlaying: false,
  currentTime: 0,
  duration: 0,
  updatedAt: 0,
  isFullscreen: false,
};

function titleOf(anime) {
  return anime?.titleText || anime?.title?.romaji || anime?.title?.english || anime?.title?.native || 'Anime seçin';
}

function formatTime(value) {
  const seconds = Math.max(0, Math.floor(Number(value) || 0));
  const minutes = Math.floor(seconds / 60);
  const rest = String(seconds % 60).padStart(2, '0');
  return `${minutes}:${rest}`;
}
function proxyImageUrl(value) {
  if (typeof value !== 'string' || !value) return '';
  if (value.startsWith('/api/images/')) return value;
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:') return '';
    return `/api/images/${url.hostname}${url.pathname}${url.search}`;
  } catch {
    return '';
  }
}
function proxyMediaUrl(value) {
  if (typeof value !== 'string' || !value) return '';
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:') return '';
    if (url.hostname.endsWith('.yeshi.eu.org')) {
      const server = url.hostname.slice(0, -'.yeshi.eu.org'.length);
      return `/media-yeshi/${server}${url.pathname}${url.search}`;
    }
    if (url.hostname.endsWith('.zyapbot.eu.org')) {
      const server = url.hostname.slice(0, -'.zyapbot.eu.org'.length);
      return `/media-zyap/${server}${url.pathname}${url.search}`;
    }
    if (url.hostname === 'x.aniziumserver.sbs' || url.hostname === 'alucard.click') {
      return `/media-proxy?url=${encodeURIComponent(value)}`;
    }
    return `/media/${url.hostname}${url.pathname}${url.search}`;
  } catch {
    return '';
  }
}


export default function App() {
  const inDiscordActivity = isDiscordActivityEnvironment();
  const [session, setSession] = useState({ status: inDiscordActivity ? 'booting' : 'ready', mode: inDiscordActivity ? 'discord' : 'blocked', sdk: null, instanceId: '' });
  const [party, setParty] = useState(emptyParty);
  const [catalog, setCatalog] = useState([]);
  const [query, setQuery] = useState('');
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sourceIndex, setSourceIndex] = useState(0);
  const [connection, setConnection] = useState('connecting');
  const [error, setError] = useState('');

  const roomIdRef = useRef('');
  const detailCacheRef = useRef(new Map());
  const videoRef = useRef(null);
  const suppressPlaybackRef = useRef(false);
  const lastPlaybackSentRef = useRef(0);
  const playerShellRef = useRef(null);
  const [playerTime, setPlayerTime] = useState(0);
  const [playerDuration, setPlayerDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [playerPaused, setPlayerPaused] = useState(true);
  const [volume, setVolume] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [videoLoading, setVideoLoading] = useState(false);

  const send = useCallback(async (payload) => {
    const roomId = roomIdRef.current;
    if (!roomId) return;
    try {
      const response = await fetch(`/api/rooms/${encodeURIComponent(roomId)}/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const next = await readJson(response, 'Oda senkronizasyonu başarısız.');
      if (next.state) {
        setParty((current) => ({ ...current, ...next.state }));
        if (typeof next.state.isFullscreen === 'boolean') {
          setIsFullscreen(next.state.isFullscreen);
        }
      }
    } catch (sendError) {
      setError(sendError.message);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    let pollingTimer;

    initializeDiscord().then(async (nextSession) => {
      if (cancelled) return;
      setSession({ ...nextSession, status: 'ready' });
      if (nextSession.mode === 'blocked') {
        setConnection('blocked');
        return;
      }

      roomIdRef.current = nextSession.instanceId;
      const refreshRoom = async () => {
        try {
          const response = await fetch(`/api/rooms/${encodeURIComponent(nextSession.instanceId)}`, { headers: { Accept: 'application/json' } });
          const next = await readJson(response, 'Oda bağlantısı kurulamadı.');
          if (cancelled) return;
          const roomState = next.state || {};
          setParty((current) => ({ ...current, ...roomState }));
          if (typeof roomState.isFullscreen === 'boolean') {
            setIsFullscreen(roomState.isFullscreen);
          }
          setConnection('connected');
        } catch (refreshError) {
          if (!cancelled) {
            setConnection('error');
            setError(refreshError.message);
          }
        }
      };
      await refreshRoom();
      pollingTimer = window.setInterval(refreshRoom, 1000);
    });

    return () => {
      cancelled = true;
      if (pollingTimer) window.clearInterval(pollingTimer);
      roomIdRef.current = '';
    };
  }, []);


  useEffect(() => {
    if (session.mode === 'blocked') {
      setCatalogLoading(false);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setCatalogLoading(true);
      try {
        const response = await searchAnime(query);
        if (!cancelled) setCatalog(response.items || []);
      } catch (loadError) {
        if (!cancelled) setError(loadError.message);
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    }, query ? 350 : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, session.mode]);

  const selectedAnimeId = party.selectedAnime?.id;
  useEffect(() => {
    let cancelled = false;
    if (!selectedAnimeId) {
      setDetail(null);
      return undefined;
    }
    const cached = detailCacheRef.current.get(selectedAnimeId);
    if (cached) {
      setDetail(cached);
      return undefined;
    }
    setDetailLoading(true);
    getAnimeDetail(selectedAnimeId)
      .then((anime) => {
        if (cancelled) return;
        detailCacheRef.current.set(selectedAnimeId, anime);
        setDetail(anime);
        setError('');
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError.message);
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedAnimeId]);

  const seasons = detail?.seasons || [];
  const selectedEpisode = useMemo(() => {
    if (!party.selectedEpisode) return null;
    for (const season of seasons) {
      const episode = season.episodes?.find(
        (item) => item.episodeNumber === party.selectedEpisode.episodeNumber && season.seasonNumber === party.selectedEpisode.seasonNumber,
      );
      if (episode) return episode;
    }
    return null;
  }, [party.selectedEpisode, seasons]);

  const selectedSource = selectedEpisode?.videoSources?.[sourceIndex] || selectedEpisode?.videoSources?.[0] || null;

  useEffect(() => {
    setSourceIndex(0);
  }, [party.selectedEpisode?.seasonNumber, party.selectedEpisode?.episodeNumber]);

  useEffect(() => {
    if (!detail || party.selectedEpisode) return;
    const firstSeason = detail.seasons?.[0];
    const firstEpisode = firstSeason?.episodes?.[0];
    if (firstEpisode) {
      const firstSelection = { seasonNumber: firstSeason.seasonNumber, episodeNumber: firstEpisode.episodeNumber };
      setParty((current) => ({ ...current, selectedEpisode: firstSelection, currentTime: 0, isPlaying: false }));
      send({ type: 'select-episode', episode: firstSelection });
    }
  }, [detail, party.selectedEpisode, send]);


  useEffect(() => {
    const video = videoRef.current;
    if (!video || !selectedEpisode) return;
    const difference = Math.abs((video.currentTime || 0) - (party.currentTime || 0));
    if (party.currentTime > 0 && difference > 1.5) video.currentTime = party.currentTime;
    if (party.isPlaying && video.paused) {
      suppressPlaybackRef.current = true;
      video.play().catch(() => {}).finally(() => {
        window.setTimeout(() => { suppressPlaybackRef.current = false; }, 150);
      });
    } else if (!party.isPlaying && !video.paused) {
      suppressPlaybackRef.current = true;
      video.pause();
      window.setTimeout(() => { suppressPlaybackRef.current = false; }, 150);
    }
  }, [party.isPlaying, party.currentTime, selectedEpisode, selectedSource]);

  const selectAnime = (anime) => {
    const selected = {
      id: anime.id || anime._id,
      slug: anime.slug,
      titleText: titleOf(anime),
      coverImage: anime.coverImage,
    };
    if (!selected.id) return;
    setParty((current) => ({ ...current, selectedAnime: selected, selectedEpisode: null, currentTime: 0, isPlaying: false }));
    send({ type: 'select-anime', anime: selected });
    setError('');
  };

  const selectEpisode = (seasonNumber, episodeNumber) => {
    const selected = { seasonNumber, episodeNumber };
    setParty((current) => ({ ...current, selectedEpisode: selected, currentTime: 0, isPlaying: false }));
    send({ type: 'select-episode', episode: selected });
  };

  const sendPlayback = (isPlaying, currentTime, duration) => {
    const next = {
      isPlaying,
      currentTime: Math.max(0, Number(currentTime) || 0),
      duration: Math.max(0, Number(duration) || 0),
    };
    setParty((current) => ({ ...current, ...next }));
    send({ type: 'playback', ...next });
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) return;
    setPlayerTime(video.currentTime || 0);
    setPlayerDuration(video.duration || 0);
    if (video.paused || suppressPlaybackRef.current) return;
    const now = Date.now();
    if (now - lastPlaybackSentRef.current < 1000) return;
    lastPlaybackSentRef.current = now;
    sendPlayback(true, video.currentTime, video.duration);
  };

  const togglePlayback = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) video.play().catch(() => {});
    else video.pause();
  };

  const seekVideo = (event) => {
    const video = videoRef.current;
    if (!video) return;
    const time = Number(event.target.value);
    video.currentTime = time;
    setPlayerTime(time);
    sendPlayback(!video.paused, time, video.duration);
  };

  const changeVolume = (event) => {
    const next = Math.max(0, Math.min(1, Number(event.target.value)));
    const video = videoRef.current;
    if (video) video.volume = next;
    setVolume(next);
    setIsMuted(next === 0);
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    const nextMuted = !video.muted;
    video.muted = nextMuted;
    setIsMuted(nextMuted);
  };

  const toggleFullscreen = () => {
    const next = !isFullscreen;
    setIsFullscreen(next);
    send({ type: 'fullscreen', isFullscreen: next });
  };
  useEffect(() => {
    const handlePlayerKeys = (event) => {
      if (!isFullscreen || !videoRef.current) return;
      const target = event.target;
      if (target instanceof HTMLElement && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        setIsFullscreen(false);
        send({ type: 'fullscreen', isFullscreen: false });
        return;
      }

      if (event.code === 'Space') {
        event.preventDefault();
        togglePlayback();
        return;
      }

      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault();
        const video = videoRef.current;
        const step = event.key === 'ArrowUp' ? 5 : -5;
        const currentVolume = video.muted ? 0 : video.volume;
        const nextVolume = Math.max(0, Math.min(100, Math.round(currentVolume * 100) + step)) / 100;
        video.volume = nextVolume;
        video.muted = nextVolume === 0;
        setVolume(nextVolume);
        setIsMuted(video.muted);
        return;
      }

      const delta = event.key === 'ArrowLeft' ? -5 : event.key === 'ArrowRight' ? 5 : 0;
      if (!delta) return;
      event.preventDefault();
      const video = videoRef.current;
      const duration = Number.isFinite(video.duration) ? video.duration : Number.MAX_SAFE_INTEGER;
      const nextTime = Math.max(0, Math.min(duration, (video.currentTime || 0) + delta));
      video.currentTime = nextTime;
      setPlayerTime(nextTime);
      sendPlayback(!video.paused, nextTime, video.duration);
    };

    window.addEventListener('keydown', handlePlayerKeys);
    return () => window.removeEventListener('keydown', handlePlayerKeys);
  }, [isFullscreen, send]);


  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.volume = volume;
    video.muted = isMuted;
  }, [selectedSource, volume, isMuted]);



  if (session.status === 'ready' && session.mode === 'blocked') {
    return (
      <main className="blocked-shell">
        <section className="blocked-card">
          <p className="blocked-brand">AniTilky</p>
          <h1>{inDiscordActivity ? 'Activity bağlantısı kurulamadı' : 'Discord Activity olarak açın'}</h1>
          <p>{inDiscordActivity ? (session.notice || 'Discord Activity yapılandırması eksik.') : 'Watch Party, AniTilky’nin Discord içindeki Activity deneyimidir. Discord’da bir ses kanalından Activity’yi başlattığınızda burada açılır.'}</p>
        </section>
      </main>
    );
  }


  const handleClose = () => closeActivity(session.sdk);

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand-lockup">
          <div>
            <p className="wordmark">AniTilky</p>
            <h1>Watch Party</h1>
          </div>
        </div>
        <div className="topbar-actions">
          <div className={`connection-pill ${connection}`}>
            <span className="status-dot" />
            {session.status === 'booting' ? 'Discord bağlanıyor' : connection === 'connected' ? 'Oda senkron' : 'Activity bekliyor'}
          </div>
          <button className="icon-button" onClick={handleClose} aria-label="Activity kapat">×</button>
        </div>
      </header>

      {error && <div className="error-bar" role="alert">{error}<button onClick={() => setError('')}>Kapat</button></div>}

      <main className="main-grid">
        <section className="watch-column">
          <div className="hero-card" style={proxyImageUrl(party.selectedAnime?.coverImage) ? { backgroundImage: `url(${proxyImageUrl(party.selectedAnime.coverImage)})` } : undefined}>
            <div className="hero-shade" />
            <div className="hero-content">
              <span className="kicker">AYNI ODADA, AYNI ANDA</span>
              <h2>{titleOf(party.selectedAnime) || 'Bir anime seçin'}</h2>
              <p>{detail?.description || 'Discord arkadaşlarınla anime seç, bölümü aç ve oynatmayı birlikte kontrol et.'}</p>
              {party.selectedEpisode && (
                <span className="episode-badge">S{party.selectedEpisode.seasonNumber} · B{party.selectedEpisode.episodeNumber}</span>
              )}
            </div>
          </div>

          <div className={`player-card ${isFullscreen ? 'player-card-fullscreen' : ''}`} ref={playerShellRef}>
            {selectedSource ? (
              <>
                <video
                  key={selectedSource.url}
                  ref={videoRef}
                  className="video-player"
                  src={proxyMediaUrl(selectedSource.url)}
                  poster={proxyImageUrl(selectedEpisode?.thumbnail || detail?.coverImage)}
                  playsInline
                  preload="metadata"
                  referrerPolicy="no-referrer"
                  onPlay={() => { setPlayerPaused(false); if (!suppressPlaybackRef.current) sendPlayback(true, videoRef.current?.currentTime, videoRef.current?.duration); }}
                  onPause={() => { setPlayerPaused(true); if (!suppressPlaybackRef.current) sendPlayback(false, videoRef.current?.currentTime, videoRef.current?.duration); }}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadStart={() => setVideoLoading(true)}
                  onCanPlay={() => setVideoLoading(false)}
                  onError={() => {
                    setVideoLoading(false);
                    setError('Video yüklenemedi. Discord Developer Portal’da /media/{host} URL eşlemesini kontrol edin.');
                  }}
                  onLoadedMetadata={() => {
                    if (videoRef.current) {
                      setPlayerDuration(videoRef.current.duration || 0);
                      if (party.currentTime > 0) videoRef.current.currentTime = party.currentTime;
                    }
                  }}
                />
                {videoLoading && <div className="video-loading" role="status">Video yükleniyor…</div>}
                <div className="player-controls">
                  <button className="player-control-button" type="button" onClick={togglePlayback} aria-label={playerPaused ? 'Oynat' : 'Duraklat'}>{playerPaused ? '▶' : 'Ⅱ'}</button>
                  <span className="player-time">{formatTime(playerTime)}</span>
                  <input className="seek-range" type="range" min="0" max={playerDuration || 0} step="0.1" value={Math.min(playerTime, playerDuration || 0)} onChange={seekVideo} aria-label="Video konumu" />
                  <span className="player-time">{formatTime(playerDuration)}</span>
                  <button className="player-control-button" type="button" onClick={toggleMute} aria-label={isMuted ? 'Sesi aç' : 'Sesi kapat'}>{isMuted ? '×' : '◖'}</button>
                  <input className="volume-range" type="range" min="0" max="1" step="0.05" value={isMuted ? 0 : volume} onChange={changeVolume} aria-label="Ses seviyesi" />
                  <button className="player-control-button fullscreen-button" type="button" onClick={toggleFullscreen} aria-label={isFullscreen ? 'Tam ekrandan çık' : 'Tam ekran'}>{isFullscreen ? '⤢' : '⛶'}</button>
                </div>
              </>
            ) : (
              <div className="player-empty">
                <div className="play-orb">▶</div>
                <h3>{detailLoading ? 'Anime detayı yükleniyor' : party.selectedEpisode ? 'Bu bölüm için video kaynağı bulunamadı' : 'İzlemek için bölüm seçin'}</h3>
                <p>{detailLoading ? 'Bölüm kaynakları hazırlanıyor.' : party.selectedEpisode ? 'Başka bir kaynak seçin veya daha sonra tekrar deneyin.' : 'Anime seçtiğinizde ilk bölüm burada oynatılır ve oynatma durumu odadaki herkese aktarılır.'}</p>
              </div>
            )}
            <div className="player-meta">
              <div>
                <span className="meta-label">Oda durumu</span>
                <strong>{party.isPlaying ? 'Oynatılıyor' : 'Beklemede'} · {formatTime(party.currentTime)}</strong>
              </div>
              <div className="source-picker">
                {(selectedEpisode?.videoSources || []).map((source, index) => (
                  <button key={`${source.url}-${index}`} className={`source-chip ${index === sourceIndex ? 'active' : ''}`} onClick={() => setSourceIndex(index)}>
                    {source.quality || 'Kaynak'} · {source.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="episodes-card">
            <div className="section-heading">
              <div>
                <span className="eyebrow">SEÇİMİ ODA PAYLAŞIR</span>
                <h3>Bölümler</h3>
              </div>
              <span className="muted">{seasons.reduce((total, season) => total + (season.episodes?.length || 0), 0)} bölüm</span>
            </div>
            {seasons.length === 0 && <p className="muted empty-copy">Bu katalog kaydında oynatılabilir bölüm bulunamadı.</p>}
            <div className="season-list">
              {seasons.map((season) => (
                <div className="season-block" key={season.seasonNumber}>
                  <div className="season-title">{season.title || `Sezon ${season.seasonNumber}`}</div>
                  <div className="episode-grid">
                    {(season.episodes || []).map((episode) => {
                      const active = party.selectedEpisode?.seasonNumber === season.seasonNumber && party.selectedEpisode?.episodeNumber === episode.episodeNumber;
                      return (
                        <button
                          className={`episode-button ${active ? 'active' : ''}`}
                          key={episode.id || episode.episodeNumber}
                          onClick={() => selectEpisode(season.seasonNumber, episode.episodeNumber)}
                        >
                          <span>{String(episode.episodeNumber).padStart(2, '0')}</span>
                          <small>{episode.title}</small>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <aside className="side-column">
          <section className="panel catalog-panel">
            <div className="section-heading compact">
              <div>
                <span className="eyebrow">KATALOG</span>
                <h3>Anime seç</h3>
              </div>
              <span className="catalog-count">{catalog.length}</span>
            </div>
            <label className="search-box">
              <span>⌕</span>
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Anime ara..." aria-label="Anime ara" />
              {query && <button onClick={() => setQuery('')} aria-label="Aramayı temizle">×</button>}
            </label>
            <div className="catalog-list">
              {catalogLoading && <div className="loading-row"><span className="spinner" /> Katalog yükleniyor</div>}
              {!catalogLoading && catalog.length === 0 && <p className="muted empty-copy">Sonuç bulunamadı.</p>}
              {catalog.map((anime) => {
                const active = party.selectedAnime?.id === (anime.id || anime._id);
                return (
                  <button className={`anime-result ${active ? 'active' : ''}`} key={anime.id || anime._id} onClick={() => selectAnime(anime)}>
                    <img src={proxyImageUrl(anime.coverImage)} alt="" />
                    <span className="result-copy">
                      <strong>{titleOf(anime)}</strong>
                      <small>{anime.status || 'Katalog'}{anime.rating ? ` · ★ ${anime.rating.toFixed(1)}` : ''}</small>
                    </span>
                    <span className="result-arrow">→</span>
                  </button>
                );
              })}
            </div>
          </section>


        </aside>
      </main>
    </div>
  );
}
